import { ReactNode, type ComponentType } from "react";

/** The base interface for subscriptions. */
export interface BaseSubscription<T> {

  /** The information for the module the subscription came from. */
  readonly module: ModuleInfo;

  /** The subscription priority. */
  readonly priority: number;

  /** The subscription type. */
  readonly type: T;
}

/** A component subscription. */
export interface ComponentSubscription extends BaseSubscription<"component"> {

  /** The component type. */
  readonly component: ComponentType;

  /** The optional subscription parameters. */
  readonly params?: unknown;
}

/** A data subscription. */
export interface DataSubscription<T = unknown> extends BaseSubscription<"data"> {

  /** The data. */
  readonly data: T;
}

/** A channel subscription. */
export type Subscription = ComponentSubscription | DataSubscription<unknown>;

/** The channel subscription action. */
export interface SubscribeAction {

  /**
   * Registers a specified component against the particular channel.
   * @param moduleId The ID of the channel module.
   * @param channelId The ID of the channel.
   * @param component The component to register.
   * @param params The optional parameters to send to the channel being subscribed to when instantiating the component.
   */
  (moduleId: string | null, channelId: string, component: ComponentType, params?: unknown): void;

  /**
   * Registers specified static data against a particular channel.
   * @param moduleId The ID of the channel module.
   * @param channelId The ID of the channel.
   * @param data The data to register.
   */
  (moduleId: string | null, channelId: string, data: object): void;
}

/** Module metadata stored in the metadata.json file. */
export interface ModuleMetadata {

  /** The module ID. */
  readonly id: string;

  /** The module name. */
  readonly name: string;

  /** The module version. */
  readonly version: ReadonlyArray<number>;

  /** The module description. */
  readonly description?: string;

  /** The module author. */
  readonly author?: string;

  /** The module copyright. */
  readonly copyright?: string;

  /** The minimum required set of permissions for the module to be included for loading. */
  readonly permissions?: ReadonlyArray<string | ReadonlyArray<string>>;

  /** Feature flags and configuration for hotkeys. */
  readonly features?: {
    readonly hotkeys?: {
      /** Whether hotkeys are enabled for this module. */
      readonly enabled?: boolean;
      /** Allowed scopes for this module's hotkeys. */
      readonly scopes?: ReadonlyArray<string>;
      /** Maximum number of hotkeys this module can register. */
      readonly maxRegistrations?: number;
      /** Priority level for this module's hotkeys. */
      readonly priority?: "low" | "normal" | "high";
      /** Strict mode - when true, blocks invalid registrations; when false, just warns. */
      readonly strictMode?: boolean;
      /**
       * Per-scope configuration overrides.
       * Allows fine-grained control over each scope's behavior.
       */
      readonly scopeConfig?: {
        readonly [scopeName: string]: {
          /** Whether this specific scope is enabled. */
          readonly enabled?: boolean;
          /** Maximum registrations for this specific scope. */
          readonly maxRegistrations?: number;
          /** Priority override for hotkeys in this scope. */
          readonly priority?: "low" | "normal" | "high";
          /** Description of what this scope is used for. */
          readonly description?: string;
        };
      };
    };
  };
}

/** A module manifest that describes a module. */
export interface ModuleManifestV1 {

  /** The module priority. If unset, defaults to 0. */
  readonly priority?: number;

  /** The manifest version. */
  readonly version: 1 | undefined,

  /** The module info. */
  readonly info: ModuleInfo;

  /** Indicates whether pages in this module should show the shell UI or not. Alternatively a list of module relative paths can be specified for routes that should not show the shell UI. If not specified, all routes show the shell UI. */
  readonly fullscreen?: boolean | ReadonlyArray<string>;

  /** Asynchronously provides the index page for the module. */
  readonly index?: ComponentType;

  /**
   * Asynchronously provides a container component to wrap around channel subscribers and the module index.
   * This is where modules should define shared or global components, for example a ReactQueryProvider,
   * that need to be applied across all components in the module.
   */
  readonly container?: ComponentType<{ readonly children?: ReactNode }>;

  /** Asynchronously initializes the module. */
  readonly onInit?: (subscribe: SubscribeAction) => void;
}


/** A module manifest that describes a module. */
export interface ModuleManifestV2 {

  /** The module priority. If unset, defaults to 0. */
  readonly priority?: number;

  /** The manifest version. */
  readonly version: 2,

  /** The module info. */
  readonly info: ModuleInfo;

  /** Indicates whether pages in this module should show the shell UI or not. Alternatively a list of module relative paths can be specified for routes that should not show the shell UI. If not specified, all routes show the shell UI. */
  readonly fullscreen?: boolean | ReadonlyArray<string>;

  /** Asynchronously provides the index page for the module. */
  readonly index?: () => Promise<ComponentType | undefined>;

  /**
   * Asynchronously provides a container component to wrap around channel subscribers and the module index.
   * This is where modules should define shared or global components, for example a ReactQueryProvider,
   * that need to be applied across all components in the module.
   */
  readonly container?: () => Promise<ComponentType<{ readonly children?: ReactNode }>>;

  /** Asynchronously initializes the module. */
  readonly onInit?: (subscribe: SubscribeAction) => Promise<void>;
}

/** A module manifest that describes a module. */
export type ModuleManifest = ModuleManifestV1 | ModuleManifestV2;

/** Module information. */
export interface ModuleInfo {

  /** The module ID. */
  readonly id: string;

  /** The module name. */
  readonly name: string;

  /** The module version. */
  readonly version: [major: number, minor: number, build?: number];

  /** The module description. */
  readonly description?: string;

  /** The module author. */
  readonly author?: string;

  /** The module copyright. */
  readonly copyright?: string;
}

/**
 * Gets the module information from module metadata.
 * @param metadata The module metadata.
 * @returns The module info.
 */
export function moduleInfoFromMetadata(metadata: ModuleMetadata): ModuleInfo {
  return (metadata as unknown) as ModuleInfo; // We actually don't need to do anything here.
}
